/**
 * Domain rule violations, carried as a typed error rather than a string so an HTTP
 * layer can map one to a status without re-deriving what went wrong, and so tests
 * assert on the code instead of on prose.
 */
export const DOMAIN_ERROR_CODES = [
  'study_not_found',
  'purchase_not_found',
  'purchase_conflict',
  'purchase_editing_held',
  'checkout_unavailable',
  'checkout_opening',
  'checkout_closed',
  'checkout_failed',
  'checkout_other_provider',
  'checkout_session_missing',
  'payment_provider_unavailable',
  'study_locked',
  'invalid_state_transition',
  'study_not_ready',
  'study_not_locked',
  'study_not_in_fieldwork',
  'study_not_analyzing',
  'brief_not_published',
  'brief_synthesis_not_retryable',
  'brief_fixture_conflict',
  'brief_wording_refused',
  'brief_summary_empty',
  'brief_method_locked',
  'brief_chat_is_follow_ups',
  'brief_export_unavailable',
  'brief_export_busy',
  'brief_export_failed',
  'follow_up_id_conflict',
  'follow_up_answer_running',
  'follow_up_not_answered',
  'follow_up_already_finalized',
  'follow_up_updating',
  'follow_up_note_needs_card',
  'follow_up_unknown_card',
  'follow_up_not_retryable',
  'study_not_deletable',
  'study_turn_running',
  'study_not_draft',
  'study_step_not_reachable',
  'study_type_unknown',
  'study_type_not_editable',
  'study_type_version_missing',
  'study_type_unknown_in_build',
  'materials_shape_missing',
  'materials_fact_unknown',
  'materials_extraction_pending',
  'materials_missing_required',
  'materials_not_editable',
  'materials_not_confirmed',
  'materials_file_not_found',
  'materials_file_not_image',
  'materials_file_not_in_inputs',
  'study_file_storage_full',
  'file_name_invalid',
  'file_too_large',
  'file_type_unsupported',
  'file_damaged',
  'image_dimensions_out_of_range',
  'cross_site_request',
  'goal_options_not_ready',
  'goal_options_changed',
  'goal_options_outdated',
  'goal_option_unknown',
  'change_unacknowledged',
  'goal_not_chosen',
  'rqs_goal_outdated',
  'rqs_upstream_changed',
  'rqs_not_approved',
  'rq_unknown',
  'study_refused',
  'policy_review_required',
  'policy_review_pending',
  'policy_review_unavailable',
  'quote_stale',
  'quote_expired',
  'quote_unavailable',
  'quote_refused',
  'study_too_large_to_simulate',
  'provisioning_unavailable',
  'artifact_not_found',
  'artifact_id_required',
  'base_version_required',
  'artifact_kind_not_writable',
  'artifact_kind_retired',
  'artifact_id_not_allowed',
  'dg_serves_unknown_rq',
  'audience_unresolvable',
  'audience_catalog_unavailable',
  'audience_catalog_not_configured',
  'audience_catalog_not_entitled',
  'audience_below_quality_floor',
  'audience_screener_invalid',
  'audience_too_many_screeners',
  'audience_screener_not_found',
  'audience_filter_not_found',
  'audience_limitation_not_found',
  'audience_upstream_changed',
  'audience_use_reshape',
  'audience_country_is_a_field',
  'audience_screening_mismatch',
  'dg_not_approved',
  'artifact_stale',
  'artifact_not_stale',
  'artifact_version_not_current',
  'dg_question_not_found',
  'dg_section_not_found',
  'dg_upstream_changed',
  'dg_rq_unserved',
  'dg_question_kind_unavailable',
  'dg_image_not_candidate',
  'dg_image_already_shown',
  'dg_image_not_current',
  'dg_coverage_not_checked',
  'dg_coverage_gap',
  'dg_coverage_uncertain',
  'user_edit_not_allowed',
  'unknown_schema_version',
  'invalid_content',
  'stored_content_invalid',
  'turn_not_found',
  'turn_spend_cap_exceeded',
  'daily_spend_cap_exceeded',
  'iteration_cap_reached',
  'agent_unavailable',
  'agent_provider_failed',
  'provisioning_position_moved',
  'provisioning_aborted',
  'provisioning_verify_failed',
  'provisioning_intent_changed',
  'provisioning_study_not_ready',
  'provisioning_launch_not_fielding',
  'provisioning_abort_too_late',
  'provisioning_already_running',
  'setup_not_retryable',
] as const;

export type DomainErrorCode = (typeof DOMAIN_ERROR_CODES)[number];

/**
 * A rejected write is a conflict with the current state of the study, not a
 * malformed request, so the lifecycle and lock refusals answer 409. Content that
 * fails its schema is the caller's mistake (400) — but the same schema failure on a
 * row we already stored is ours, not theirs, so the read path has its own code and
 * answers 500. Otherwise a corrupt row and a bad request are indistinguishable in
 * the logs, and the one that needs a human looks like the one that doesn't.
 */
const STATUS_BY_CODE: Record<DomainErrorCode, number> = {
  study_not_found: 404,
  purchase_not_found: 404,
  purchase_conflict: 409,
  purchase_editing_held: 409,
  checkout_unavailable: 503,
  // A checkout session that is still being opened: another request for the same purchase holds the
  // provider's idempotency key, or one may still be on its way. Nothing is wrong with the request,
  // and the same call answers once the opening settles.
  checkout_opening: 503,
  // Cancelling a checkout the purchase has already left: it was paid, or closed. A conflict with the
  // purchase's state, refused on every retry.
  checkout_closed: 409,
  // A checkout session that could not be opened for a reason no retry clears — the provider refused
  // it. The attempt is cancelled and the Study released, so a new Pay starts over; the failure is
  // the provider's answer rather than the request's.
  checkout_failed: 502,
  // Cancelling a checkout opened through a payment provider this environment no longer uses. A
  // conflict with how the purchase was opened, refused on every retry; the attempt is released on
  // its own once it expires.
  checkout_other_provider: 409,
  // A checkout whose session the payment provider no longer knows: it cannot be reopened, and no
  // retry changes that. The attempt is released on its own once it is past its end.
  checkout_session_missing: 409,
  // The payment provider could not be reached. The purchase is as it was, and the same call can
  // succeed later.
  payment_provider_unavailable: 503,
  study_locked: 409,
  invalid_state_transition: 409,
  study_not_ready: 409,
  study_not_locked: 409,
  // A study the local fieldwork route cannot hold in fieldwork: a draft, or one fieldwork has
  // finished with. A conflict with the study's state, left as it was.
  study_not_in_fieldwork: 409,
  // A Brief asked for of a study whose answers are not being read: before fieldwork has ended, or
  // once the study has finished. A conflict with the study's state, not a bad request.
  study_not_analyzing: 409,
  // A study moved to complete by anything but the publication of its Brief, which is the only
  // move that completes one.
  brief_not_published: 409,
  // Try again asked of a Brief that is being written, is written, or was declined below the
  // Quality floor: only one that could not be written may be asked for again.
  brief_synthesis_not_retryable: 409,
  // A Brief's PDF asked of a process that cannot make one: no browser to render it, or no built
  // page to render it from. The environment rather than the request, and the same request can
  // succeed once it has both.
  brief_export_unavailable: 503,
  // A Brief's PDF asked while the process is already making as many as it takes at once, with as
  // many waiting as it queues. Nothing is wrong with the request; asking again shortly can succeed.
  brief_export_busy: 503,
  // A Brief's PDF that was started and could not be finished: the renderer crashed, the page failed
  // or the render ran out of time. Nothing was kept, and asking again can succeed.
  brief_export_failed: 503,
  // The local fixture route asked for a Brief over other evidence than the request the study
  // already holds: it does not replace a request that is pending, published or declined.
  brief_fixture_conflict: 409,
  // Verdict wording the researcher wrote that the Brief's gate refuses, or that is too long or holds
  // no words. The request's own content is wrong, so it is a bad request; `detail.rule` says which.
  brief_wording_refused: 400,
  // Hiding the only summary bullet still showing: a Brief always shows at least one. A conflict with
  // the Brief as it stands, since the same request succeeds once another bullet shows.
  brief_summary_empty: 409,
  // A revision whose Method is not the Brief's: the study as it was run does not change.
  brief_method_locked: 409,
  // A turn sent on the Brief's thread, where every question is a follow-up: the wrong request.
  brief_chat_is_follow_ups: 400,
  // A follow-up's id, answer or outcome in a state that does not allow the request: each a conflict
  // with what is stored, which a re-read shows (`FOLLOW_UP_REFUSAL_CODES`).
  follow_up_id_conflict: 409,
  follow_up_answer_running: 409,
  follow_up_not_answered: 409,
  follow_up_already_finalized: 409,
  follow_up_updating: 409,
  follow_up_not_retryable: 409,
  // A note with no card to sit under, or a card the Brief does not have: what was sent is wrong.
  follow_up_note_needs_card: 400,
  follow_up_unknown_card: 400,
  study_not_deletable: 409,
  // A study with a reply still being written cannot be deleted out from under the worker that is
  // writing it. A conflict with state rather than a bad request, and the same call succeeds once
  // that turn ends — which is the difference between this and the two refusals above it.
  study_turn_running: 409,
  // A study whose title is fixed because it has left draft. A conflict with the study's state
  // rather than a bad request: nothing the caller sent is wrong, and the same study is refused on
  // every retry.
  study_not_draft: 409,
  // A step the study has not got to. A conflict with where the study is rather than a bad request:
  // the slug is a real step, and the same move is refused until the work in front of it is done.
  study_step_not_reachable: 409,
  // A Study Type or Category the catalogue does not hold. A bad request rather than a conflict: no
  // study can ever be of a type the product does not have, so nothing about the study would make
  // it acceptable — the same argument the step slug above is refused on.
  study_type_unknown: 400,
  // A study whose type is fixed because it has been paid for, frozen or launched. A conflict with
  // the study's state rather than a bad request: the type asked for is a real one, and the same
  // study is refused on every retry.
  study_type_not_editable: 409,
  // A stored type or template version this build cannot resolve. Ours rather than the caller's,
  // like the stored content that fails its schema below, and answered opaquely for the same
  // reason: a row nothing can read needs a human, and resolving the newest template instead would
  // silently change a study already in progress.
  study_type_version_missing: 500,
  // A stored Study Type this build's catalogue does not name, which is a different divergence from
  // the one above: the version is not the problem, the id is. Ours rather than the caller's for the
  // same reason, and answered the same way — but an operator reading the code goes to the catalogue
  // rather than to the template registry.
  study_type_unknown_in_build: 500,
  // A study with no Materials shape to work against: one with no Study Type yet, or one pinned to
  // a template version that predates the Materials block. A conflict with the study rather than a
  // bad request, and one the researcher can resolve: picking the type, or picking it again, pins a
  // version that has one.
  materials_shape_missing: 409,
  // A fact the study's Materials shape does not have. Like a guide question that is not there, the
  // thing addressed does not exist, and no retry makes it exist.
  materials_fact_unknown: 404,
  // Materials cannot be confirmed while Sigora is still reading them. A conflict with state that
  // will move on its own: the same confirmation is accepted once the reading settles.
  materials_extraction_pending: 409,
  // Materials missing a fact the Study Type needs. A conflict with the study rather than a bad
  // request: the confirmation is well-formed, and what has to change is a row or an input.
  materials_missing_required: 409,
  // Materials change only while the study is a draft nobody has locked, launched or paid for. A
  // conflict with the study's state, refused on every retry until the state changes.
  materials_not_editable: 409,
  // Something that needs the study's Materials confirmed at the version it holds now, asked of a
  // study whose confirmation is missing or has been overtaken by a change.
  materials_not_confirmed: 409,
  // Materials naming a file this study never stored, or naming it as the wrong kind. What is wrong
  // is the inputs that were sent, and they stay wrong however the study moves.
  materials_file_not_found: 400,
  // A Stimulus choice naming a document: only an image can be shown to participants.
  materials_file_not_image: 400,
  // A Stimulus choice naming a file the current Materials do not hold. The repair is to add it.
  materials_file_not_in_inputs: 400,
  // A study that has stored as many file bytes as it may keep. A conflict with the study rather
  // than a bad file: the same upload is refused on every retry, and nothing a researcher removes
  // frees the space, since a draft study keeps every file it was given.
  study_file_storage_full: 409,
  // The five below refuse an upload for what the file is. 400 for a file that is wrong as it stands,
  // and 413 for one that is too large, which is the status a proxy in front answers it with too.
  file_name_invalid: 400,
  file_too_large: 413,
  file_type_unsupported: 400,
  file_damaged: 400,
  image_dimensions_out_of_range: 400,
  // A write a browser says another site started. Refused before anything is read, on the routes that
  // make something permanent for a draft study's life; the same request from the study's own page
  // is accepted.
  cross_site_request: 403,
  // A Goal chosen before there are any options to choose from. A conflict with the study rather
  // than a bad request: the same choice is accepted once a set has been drafted.
  goal_options_not_ready: 409,
  // A choice from a set the study no longer holds, which is what a tab that loaded before the
  // options were drafted again or reshaped sends. The repair is to look at the current set.
  goal_options_changed: 409,
  // A choice from the set on the card when that set was drafted for a Study Type, template or
  // Materials version the study no longer has. A conflict with the study rather than a bad request:
  // the repair is new options for the study as it stands, which the step asks for.
  goal_options_outdated: 409,
  // An option the set does not hold. Like a fact the Materials do not have, the thing addressed does
  // not exist, and no retry makes it exist.
  goal_option_unknown: 404,
  // A change of Goal that would redraft the study's research questions, guide and audience, sent
  // without saying the researcher was shown that. A conflict with the study rather than a bad
  // request: the same choice is accepted once it carries the acknowledgement.
  change_unacknowledged: 409,
  // Something that needs the study's goal chosen, approved and current, asked of a study with no
  // goal yet or one whose goal is waiting on a change. The repair is a choice on the Goal step.
  goal_not_chosen: 409,
  // Research questions naming a Goal that is not the study's current chosen one. A conflict with
  // the study rather than a bad request: questions drafted for an earlier goal are redrafted on the
  // Questions step, never edited into shape.
  rqs_goal_outdated: 409,
  // A structural change asked of research questions still waiting on a change above them: they
  // are updated on the Questions card first, and reshaped after that.
  rqs_upstream_changed: 409,
  // Something that returns a guide to approved, asked while the research questions it is built on
  // are not approved at their current version with nothing outstanding. The repair is approving
  // the questions first.
  rqs_not_approved: 409,
  // A research question the set does not hold. Like an option the Goal set does not hold, the thing
  // addressed does not exist, and no retry makes it exist.
  rq_unknown: 404,
  // A study the platform will not run. Like the other lifecycle refusals it is a conflict with what
  // is being asked rather than a malformed request, and the same study will be refused on a retry.
  // Its detail is `{ study_id, refusals: [{ step, category, guidance }] }`, each refusal naming the
  // step that holds it, and its message is the first refusal as the researcher reads it.
  study_refused: 409,
  // No policy review answers for the study as it stands: none was asked for, the one held judged
  // other versions or another policy, or one was left pending past its wait. A conflict, because
  // the study is what has to be reviewed, and the repair is to ask from Launch.
  policy_review_required: 409,
  // The study is being reviewed. A conflict rather than unavailability: nothing is down, the
  // answer is simply not in yet.
  policy_review_pending: 409,
  // The review could not be had or its answer could not be read. 503 because it is Sigora's
  // reviewer rather than the study that failed, and the same request can succeed once it answers.
  policy_review_unavailable: 503,
  // The quote the caller accepted does not price the study as it stands now: its inputs moved, or
  // it is not one of this study's quotes. A conflict rather than a bad request: the number was
  // right when it was shown, and the repair is to show the current one rather than to correct
  // anything the caller sent.
  quote_stale: 409,
  // The issued quote the caller accepted has passed the end of its guarantee window. A conflict
  // for the reason `quote_stale` is one: the number was good when it was shown, and the repair is
  // to issue the current one and show it before anything is agreed to.
  quote_expired: 409,
  // The price could not be worked out: the panel cost could not be read, or this environment has
  // nowhere to read it from. 503 because it is the environment rather than the study that failed,
  // and the same request can succeed once Remesh answers.
  quote_unavailable: 503,
  // The price was refused for good: an N no flex study takes, a workspace that cannot buy
  // recruitment, or any other refusal of Remesh's. Unprocessable rather than unavailable, because
  // asking again changes nothing; `detail.reason` says which, and what to change.
  quote_refused: 422,
  // The study asks for more participants than a simulated run can generate. A conflict rather than
  // a bad request for the same reason as the one above: nothing the caller sent is malformed, and
  // what has to change is the study's own sample size rather than anything about the call. It is
  // refused here, in front of the payment, because the only other thing that refuses it is the
  // simulation trigger — which runs after the charge, the freeze and the launch, leaving a study
  // fielding with nothing able to answer it.
  study_too_large_to_simulate: 409,
  // Nothing about the request is wrong and nothing about the study is: this deployment has no
  // worker to run it. 503 rather than 409 because it is the environment that is unavailable, and
  // the same call succeeds once it is configured.
  provisioning_unavailable: 503,
  artifact_not_found: 404,
  artifact_id_required: 400,
  // A required argument was left out. Deliberately not `artifact_version_not_current`: no version was
  // compared, and sharing that code would make a routine omission indistinguishable — in the text the
  // model reads, and in logs — from the one refusal that means the researcher edited this meanwhile.
  base_version_required: 400,
  // A kind this entry point does not write. 400 like the sibling above and for the same reason: it
  // is the request that is wrong, the fix is to send a different kind, and — decisively — a body
  // refusal comes back as data the model can read while a schema rejection ends the whole turn.
  artifact_kind_not_writable: 400,
  // A kind the store reads and refuses to write, on every path. 400 for the same reason as the
  // sibling above: no state of the study makes the write acceptable.
  artifact_kind_retired: 400,
  artifact_id_not_allowed: 400,
  // A document naming a research question the study does not have. 400 rather than 409: what is
  // wrong is the document that was submitted, and it stays wrong however the study moves — the fix
  // is to name a research question that exists, not to retry.
  dg_serves_unknown_rq: 400,
  // An audience naming a demographic, or an answer to one, that the panel does not have. 400 for the
  // same reason as the traceability refusals above: what is wrong is the document, and it stays wrong
  // however the study moves — the fix is to name something the catalog has, or to say in the caveats
  // that the intent cannot be targeted.
  audience_unresolvable: 400,
  // The catalog could not be read, so nothing about the audience could be established either way.
  // Upstream's, not the caller's, and the same request may well succeed a minute later — which is
  // why it is not `audience_unresolvable`: telling a researcher their audience is wrong when what
  // actually happened is that Remesh was unreachable would send them editing a correct document.
  audience_catalog_unavailable: 503,
  // A recruit environment with no panel to check against, which can never provision: nothing can be
  // approved here, and unlike an outage no wait changes that.
  audience_catalog_not_configured: 503,
  // A workspace the panel will not recruit for. Permanent until the workspace is entitled.
  audience_catalog_not_entitled: 409,
  // An audience asking for fewer people than the Quality floor. A conflict with the rule rather than
  // a malformed document: the count is well-formed, and the repair is a larger one.
  audience_below_quality_floor: 409,
  // A screening question whose structure cannot screen: too few or too many options, two options
  // worded the same, or options that turn nobody away or everybody away. What is wrong is the
  // document, and it stays wrong however the study moves.
  audience_screener_invalid: 400,
  audience_too_many_screeners: 400,
  // A screener, filter or Limitation the audience does not hold. Like a guide question that is not
  // there, the thing addressed does not exist, and no retry makes it exist.
  audience_screener_not_found: 404,
  audience_filter_not_found: 404,
  audience_limitation_not_found: 404,
  // A change asked of an audience still waiting on a change above it: the upstream change is
  // answered first, by a proposed fix or a redraft, and the audience is changed after that.
  audience_upstream_changed: 409,
  // A whole audience written over one that stands. A conflict with the audience's state: a whole
  // document is for a first draft or an upstream change, and a change is asked of reshape_audience.
  audience_use_reshape: 409,
  // An audience naming the country demographic as a filter: countries are the audience's own field.
  audience_country_is_a_field: 400,
  // A conversation whose screening questions do not read back as the audience that was applied.
  // Not retryable: something has to be reconciled before the study can field.
  audience_screening_mismatch: 409,
  // Something that needs the guide approved at its current version, asked of a study whose guide is
  // not. The repair is approving the guide first.
  dg_not_approved: 409,
  artifact_stale: 409,
  // A window named as answered that is no longer outstanding: another version answered that change
  // first, which is a conflict with state that has since moved rather than a bad request.
  artifact_not_stale: 409,
  artifact_version_not_current: 409,
  dg_question_not_found: 404,
  dg_section_not_found: 404,
  // A structural change asked of a guide still waiting on a change above it: the upstream change is
  // answered first, by a redraft or a proposed fix, and the guide is reshaped after that.
  dg_upstream_changed: 409,
  // A change that would leave a research question with nothing in the guide serving it. The floor
  // under shortening a guide: the research question is removed on its own step, not starved here.
  dg_rq_unserved: 409,
  // A question type the guide can hold but no agent-authored write may add yet.
  dg_question_kind_unavailable: 409,
  // An image asked to be shown that is not one of the images the researcher marked to show on the
  // current Materials. One code whatever the file is — another study's, a document, an unknown id
  // or a replaced image's old one — so the answer never says which.
  dg_image_not_candidate: 409,
  // An image asked to be shown on a second question: each image is shown once.
  dg_image_already_shown: 409,
  // A guide showing an image that no longer resolves to one the researcher marked to show. The
  // repair is a change to the guide, which removes or replaces the image.
  dg_image_not_current: 409,
  // An approval of a guide nobody has checked against the research questions as they stand now. A
  // conflict with the study rather than a bad request: the same approval goes through once the check
  // for this version has answered.
  dg_coverage_not_checked: 409,
  // An approval of a guide the check found leaves a research question unanswered. The repair is a
  // change to the guide, which is a new version and a new check; no retry of the approval clears it.
  dg_coverage_gap: 409,
  // An approval of a guide the check could not say answers every research question, sent without
  // saying the researcher chose to approve anyway. The same approval goes through once it does.
  dg_coverage_uncertain: 409,
  user_edit_not_allowed: 409,
  unknown_schema_version: 500,
  invalid_content: 400,
  stored_content_invalid: 500,
  turn_not_found: 404,
  // A spend cap is a budget that is momentarily exhausted rather than a request that was wrong,
  // and 429 is the status whose retry semantics match: the same request may succeed later.
  turn_spend_cap_exceeded: 429,
  daily_spend_cap_exceeded: 429,
  iteration_cap_reached: 409,
  // No provider is configured, so the work cannot be attempted at all. Distinct from a failure
  // during a turn: nothing was spent and nothing is wrong with the request.
  agent_unavailable: 503,
  // The provider was reachable and refused or broke. Upstream's fault rather than ours or the
  // caller's, which is what 502 says and what tells an operator where to look.
  agent_provider_failed: 502,
  // A second provisioning run moved the record between this one reading its position and writing
  // the next one. A conflict with state that has moved, and the loser must not simply try again:
  // the step it was about to take may already have happened.
  provisioning_position_moved: 409,
  // A step was asked of a run whose audience has been torn down. The run is over; a new one
  // starts from a new conversation rather than resuming this record.
  provisioning_aborted: 409,
  // The audience artifact moved after the run snapshotted it — its number of completes or the
  // filters themselves. A conflict with state that has changed under the run, and not one this side
  // may adopt: the conversation exists for the old audience, and the new one would price and freeze
  // a study Remesh is not fielding.
  provisioning_intent_changed: 409,
  // Provisioning was asked for a study that is not approved and ready. A conflict with the state of
  // the study rather than a bad request: nothing about the call is wrong, and the same call succeeds
  // once the study's artifacts are approved.
  provisioning_study_not_ready: 409,
  // Remesh's record of the study disagrees with the intent it was provisioned from. Not a bad
  // request and not retryable — something has to be reconciled before this study can field.
  provisioning_verify_failed: 409,
  // The launch answered with a status that means fielding is over, so the study a resumed run
  // was retrying is not the study it thought it had.
  provisioning_launch_not_fielding: 409,
  // Abort is the pre-launch undo only. Once the study is fielding there is nothing this side can
  // undo, and the public surface has no path to stop it.
  provisioning_abort_too_late: 409,
  // A restart was asked for a study whose run has not stopped. A conflict with state rather than a
  // bad request: the same call succeeds once the run this one would have duplicated has stopped.
  provisioning_already_running: 409,
  // A setup retry was asked of a study with no paid launch to retry, or one stopped for a reason a
  // retry cannot change — a refunded purchase, a launch mode this environment does not run. A
  // conflict with state: nothing about the call is wrong.
  setup_not_retryable: 409,
};

export class DomainError extends Error {
  readonly code: DomainErrorCode;
  readonly status: number;
  /** Machine-readable specifics — which artifact, which structural field moved. */
  readonly detail: Record<string, unknown>;

  constructor(code: DomainErrorCode, message: string, detail: Record<string, unknown> = {}) {
    super(message);
    this.name = 'DomainError';
    this.code = code;
    this.status = STATUS_BY_CODE[code];
    this.detail = detail;
  }
}

export const isDomainError = (error: unknown): error is DomainError => error instanceof DomainError;
